import { useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Form } from '@/components/ui/form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import Input from '@/components/form/Input';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';

import {
  usePutOfferingCategory,
  useOfferingCategories
} from '@/data/offeringCategory';

const formSchema = z.object({
  label: z.string().min(1, { message: 'Label is required' })
});

const EditOfferingCategoryDialog = ({ category, setOpen }) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const putOfferingCategory = usePutOfferingCategory();
  const offeringCategories = useOfferingCategories();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: category.label
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const updatedCategory = await putOfferingCategory(category.id, values);
      await offeringCategories.mutate(
        (currentOfferingCategories) =>
          currentOfferingCategories.map((cat) =>
            cat.id === updatedCategory.id ? updatedCategory : cat
          ),
        false
      );
      setOpen(false);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={setOpen}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Offering Category</DialogTitle>
          <DialogDescription>
            Edit the offering category details.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <Input name="label" label="Label" autoComplete="off" />
            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>Save Changes</SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditOfferingCategoryDialog;
